import { useState, useEffect } from 'react'

export type Theme = 'light' | 'dark' | 'system'

function resolveIsDark(t: Theme): boolean {
  return t === 'dark' || (t === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
}

export function useDarkMode() {
  const [theme, setThemeState] = useState<Theme>(() => {
    return (localStorage.getItem('recrutpro_theme') as Theme) ?? 'system'
  })

  const [isDark, setIsDark] = useState(() => resolveIsDark(
    (localStorage.getItem('recrutpro_theme') as Theme) ?? 'system'
  ))

  useEffect(() => {
    const dark = resolveIsDark(theme)
    setIsDark(dark)
    document.documentElement.classList.toggle('dark', dark)
    localStorage.setItem('recrutpro_theme', theme)
  }, [theme])

  // Écouter les changements de préférence système quand theme === 'system'
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const dark = mq.matches
      setIsDark(dark)
      document.documentElement.classList.toggle('dark', dark)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = (t: Theme) => setThemeState(t)

  // Cycle : system → light → dark → system
  const cycleTheme = () =>
    setThemeState(t => t === 'system' ? 'light' : t === 'light' ? 'dark' : 'system')

  // Toggle binaire (compatibilité)
  const toggle = () => setThemeState(t => resolveIsDark(t) ? 'light' : 'dark')

  return { theme, isDark, setTheme, cycleTheme, toggle }
}
